import { zodResolver } from '@hookform/resolvers/zod';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import PercentRounded from '@mui/icons-material/PercentRounded';
import ReceiptRounded from '@mui/icons-material/ReceiptRounded';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { api, ApiError, unwrap } from '../../../api/client';
import { StatusChip } from '../../../components/StatusChip';
import { useNotify } from '../../../lib/notify';
import {
  idempotency,
  keys,
  useSaveSettings,
  useTaxRates,
  type ShopSettings,
  type TaxRate,
} from '../api';
import { applyFieldErrors } from '../lib';
import { SaveBar, SectionCard, SettingSwitch } from '../ui';

const schema = z.object({
  taxEnabled: z.boolean(),
  pricesIncludeTax: z.boolean(),
  taxNumber: z.string().trim().max(50, 'At most 50 characters'),
});
type Values = z.infer<typeof schema>;

function toValues(s: ShopSettings): Values {
  return {
    taxEnabled: s.taxEnabled,
    pricesIncludeTax: s.pricesIncludeTax,
    taxNumber: s.taxNumber ?? '',
  };
}

/** "17.0000" → "17", "7.5000" → "7.5". */
export function formatRate(rate: string): string {
  const n = Number(rate);
  return Number.isFinite(n) ? String(Number(n.toFixed(4))) : rate;
}

export function TaxTab({ settings, canManage }: { settings: ShopSettings; canManage: boolean }) {
  const notify = useNotify();
  const save = useSaveSettings();
  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm<Values>({ resolver: zodResolver(schema), values: toValues(settings) });

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        taxEnabled: v.taxEnabled,
        pricesIncludeTax: v.pricesIncludeTax,
        taxNumber: v.taxNumber.trim() || null,
      });
      notify.success('Tax settings saved');
    } catch (e) {
      if (!applyFieldErrors(e, ['taxNumber'], setError)) notify.error(e);
    }
  });

  const enabled = watch('taxEnabled');
  const ro = !canManage;

  return (
    <Stack spacing={2.5}>
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <SectionCard
          icon={<ReceiptRounded />}
          title="Sales tax"
          description="Charge tax on invoices and show your registration number on documents."
        >
          <Controller
            control={control}
            name="taxEnabled"
            render={({ field }) => (
              <SettingSwitch
                title="Charge tax"
                description="Adds a tax column to sales and purchase documents."
                checked={field.value}
                onChange={field.onChange}
                disabled={ro}
              />
            )}
          />
          <Collapse in={enabled}>
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
              <Controller
                control={control}
                name="pricesIncludeTax"
                render={({ field }) => (
                  <SettingSwitch
                    title="Prices include tax"
                    description={
                      field.value
                        ? 'Selling prices already contain tax; it is worked out from the total.'
                        : 'Tax is added on top of your selling prices.'
                    }
                    checked={field.value}
                    onChange={field.onChange}
                    disabled={ro}
                  />
                )}
              />
              <Box sx={{ pt: 1, maxWidth: 360 }}>
                <TextField
                  label="Tax registration number"
                  placeholder="e.g. NTN 1234567-8"
                  disabled={ro}
                  {...register('taxNumber')}
                  error={Boolean(errors.taxNumber)}
                  helperText={errors.taxNumber?.message ?? 'Printed on invoices when set.'}
                />
              </Box>
            </Box>
          </Collapse>
        </SectionCard>
        <SaveBar
          dirty={isDirty}
          saving={save.isPending}
          disabled={ro}
          onReset={() => reset(toValues(settings))}
        />
      </form>
      <TaxRatesCard canManage={canManage} />
    </Stack>
  );
}

function TaxRatesCard({ canManage }: { canManage: boolean }) {
  const { data, isLoading } = useTaxRates();
  const [editing, setEditing] = useState<TaxRate | 'new' | null>(null);
  const qc = useQueryClient();
  const notify = useNotify();
  const toggle = useMutation({
    mutationFn: (r: TaxRate) =>
      unwrap(
        api.PATCH('/tax-rates/{id}', {
          params: { path: { id: r.id } },
          body: { isActive: !r.isActive, version: r.version },
        }),
      ),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: keys.taxRates });
      notify.success(`${r.name} ${r.isActive ? 'activated' : 'deactivated'}`);
    },
    onError: (e) => notify.error(e),
  });
  const rates = data?.data ?? [];

  return (
    <SectionCard
      icon={<PercentRounded />}
      title="Tax rates"
      description="The rates you can pick on products and document lines."
      action={
        canManage && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddRounded />}
            onClick={() => setEditing('new')}
          >
            Add rate
          </Button>
        )
      }
    >
      {isLoading ? (
        <Stack spacing={1}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : rates.length === 0 ? (
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            borderRadius: '14px',
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>No tax rates yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
            Add the rates you charge, such as GST 17% or a reduced 5%.
          </Typography>
          {canManage && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              Add your first rate
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: 'minmax(0,1fr)',
              sm: 'repeat(2, 1fr)',
              xl: 'repeat(3, 1fr)',
            },
          }}
        >
          {rates.map((r) => (
            <Stack
              key={r.id}
              direction="row"
              spacing={1.5}
              sx={{
                alignItems: 'center',
                p: 1.5,
                pl: 2,
                borderRadius: '14px',
                border: 1,
                borderColor: 'divider',
                opacity: r.isActive ? 1 : 0.65,
                transition: 'opacity 160ms, border-color 160ms',
                '&:hover': { borderColor: 'grey.300' },
              }}
            >
              <Typography
                className="tabular"
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 22,
                  fontWeight: 750,
                  letterSpacing: '-0.02em',
                  minWidth: 64,
                  color: r.isActive ? 'primary.main' : 'text.secondary',
                }}
              >
                {formatRate(r.rate)}%
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontWeight: 600 }}>
                  {r.name}
                </Typography>
                <StatusChip status={r.isActive ? 'ACTIVE' : 'INACTIVE'} />
              </Box>
              {canManage && (
                <>
                  <Tooltip title={r.isActive ? 'Deactivate' : 'Activate'}>
                    <Switch
                      size="small"
                      checked={r.isActive}
                      disabled={toggle.isPending}
                      onChange={() => toggle.mutate(r)}
                      slotProps={{ input: { 'aria-label': `${r.name} active` } }}
                    />
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      aria-label={`Edit ${r.name}`}
                      onClick={() => setEditing(r)}
                    >
                      <EditRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Stack>
          ))}
        </Box>
      )}
      {editing && (
        <TaxRateDialog rate={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </SectionCard>
  );
}

export const taxRateSchema = z.object({
  name: z.string().trim().min(1, 'Enter a name').max(100),
  rate: z
    .string()
    .trim()
    .regex(/^\d{1,15}(\.\d{1,4})?$/, 'A percentage like 17 or 7.5 (up to 4 decimals)')
    .refine((v) => Number(v) <= 100, 'At most 100%'),
});
type RateValues = z.infer<typeof taxRateSchema>;

function TaxRateDialog({ rate, onClose }: { rate: TaxRate | null; onClose: () => void }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RateValues>({
    resolver: zodResolver(taxRateSchema),
    defaultValues: { name: rate?.name ?? '', rate: rate ? formatRate(rate.rate) : '' },
  });

  const onSubmit = handleSubmit(async (v) => {
    try {
      if (rate)
        await unwrap(
          api.PATCH('/tax-rates/{id}', {
            params: { path: { id: rate.id } },
            body: { name: v.name, rate: v.rate, version: rate.version },
          }),
        );
      else
        await unwrap(
          api.POST('/tax-rates', {
            params: idempotency(),
            body: { name: v.name, rate: v.rate },
          }),
        );
      await qc.invalidateQueries({ queryKey: keys.taxRates });
      notify.success(rate ? 'Tax rate updated' : 'Tax rate added');
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'TAX_RATE_NAME_TAKEN') {
        setError('name', { message: 'A tax rate with this name already exists' });
        return;
      }
      if (!applyFieldErrors(e, ['name', 'rate'], setError)) notify.error(e);
    }
  });

  return (
    <Dialog open onClose={isSubmitting ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {rate ? 'Edit tax rate' : 'Add tax rate'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              placeholder="e.g. GST"
              autoFocus
              {...register('name')}
              error={Boolean(errors.name)}
              helperText={errors.name?.message}
            />
            <TextField
              label="Rate"
              inputMode="decimal"
              placeholder="17"
              {...register('rate')}
              error={Boolean(errors.rate)}
              helperText={
                errors.rate?.message ??
                (rate ? 'Changing a rate affects new documents only.' : undefined)
              }
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={isSubmitting}>
            {rate ? 'Save' : 'Add rate'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
